import { Tag } from 'antd';
import dayjs from 'dayjs';

export const getBase64 = (file: any): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export const getColorFormattedText = (
  text: string,
  formattedPart: string,
  styleClasses: string
) => {
  return text.replace(formattedPart, `<span class="${styleClasses}">${formattedPart}</span>`);
};

export const hexToRGBA = (hex: string, opacity: number): string => {
  hex = hex.replace(/^#/, '');

  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const getFileNameFromUrl = (url: string) => {
  return url.substring(url.lastIndexOf('/') + 1);
};

export const parseRelativeDate = (relative: string): dayjs.Dayjs | undefined => {
  const regex = /^relative:([+-]?\d+y)?\s*([+-]?\d+m)?$/;
  const match = relative.match(regex);
  if (!match) return undefined;
  const [, yearsStr, monthsStr] = match;
  let date = dayjs();
  if (yearsStr) {
    const years = parseInt(yearsStr.replace('y', ''), 10);
    date = date.add(years, 'year');
  }
  if (monthsStr) {
    const months = parseInt(monthsStr.replace('m', ''), 10);
    date = date.add(months, 'month');
  }
  return date;
};

export const convertMinutesToHours = (t: any, minutes: number): { full: string; short: string } => {
  if (minutes <= 0) {
    const zeroMinutes = `0 ${t('common:min')}`;
    return { full: zeroMinutes, short: zeroMinutes };
  }

  let days = Math.floor(minutes / 1440);
  let hours = Math.floor((minutes % 1440) / 60);
  let remainingMinutes = Math.floor(minutes % 60);

  let fullResult = '';
  let shortResult = '';

  if (days > 0) {
    fullResult += `${days} ${t('common:day')}`;
    if (hours > 0) fullResult += ` ${hours} ${t('common:hr')}`;
    if (remainingMinutes > 0) fullResult += ` ${remainingMinutes} ${t('common:min')}`;
  } else if (hours > 0) {
    fullResult += `${hours} ${t('common:hr')}`;
    if (remainingMinutes > 0) fullResult += ` ${remainingMinutes} ${t('common:min')}`;
  } else {
    fullResult += `${remainingMinutes} ${t('common:min')}`;
  }

  if (days > 0) {
    shortResult += `${days} ${t('common:day')}`;
    if (hours > 0) shortResult += ` ${hours} ${t('common:hr')}`;
  } else if (hours > 0) {
    shortResult += `${hours} ${t('common:hr')}`;
    if (remainingMinutes > 0) shortResult += ` ${remainingMinutes} ${t('common:min')}`;
  } else {
    shortResult += `${remainingMinutes} ${t('common:min')}`;
  }

  return {
    full: fullResult.trim(),
    short: shortResult.trim(),
  };
};

interface StatusOption {
  name: string;
  value: string;
  color: string;
}

export const getStatusTag = (
  value: string,
  t: any,
  i18Namespace: string,
  options?: StatusOption[]
) => {
  const match = options?.find((option) => option.value === value);
  const color = match?.color || '#1F5A95';
  if (!value) {
    return <div className="text-center text-gray-400">-</div>;
  }

  return (
    <div className="flex flex-col items-center justify-center">
      <Tag
        className="flex items-center px-3 text-center"
        style={{
          borderColor: color,
          color: color,
          borderWidth: 0.3,
          borderRadius: 50,
          background: hexToRGBA(color, 0.2),
        }}
      >
        {t(`${i18Namespace}:${value}`)}
      </Tag>
    </div>
  );
};

export const trimValues = (obj: any): any => {
  if (typeof obj === 'string') {
    return obj.trim();
  }
  if (Array.isArray(obj)) {
    return obj.map(trimValues);
  }
  if (dayjs.isDayjs(obj)) {
    return obj.format('YYYY-MM-DD');
  }
  if (obj instanceof Date) {
    return obj.toISOString().split('T')[0];
  }
  if (typeof obj === 'object' && obj !== null) {
    return Object.fromEntries(Object.entries(obj).map(([key, val]) => [key, trimValues(val)]));
  }
  return obj;
};

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

interface FilterItem {
  key: string; // The unique identifier/name for the filter
  label: string; // The user-friendly display name )(pass the translated terms)
  value: any; // The actual value(s) selected (can be a string, number, array of strings, etc.)
}

interface ChartData {
  series: { name: string; data: any[] }[];
  categories: any[];
}

export const downloadCustomCSV = (
  fileName: string,
  chartData: ChartData,
  appliedFilters: FilterItem[],
  t: (key: string) => string,
  categoryName: string
) => {
  const { series, categories } = chartData;

  const safeValue = (val: any, emptyValue: string = t('notSelected')): string | any[] => {
    if (val === undefined || val === null) return emptyValue;
    if (Array.isArray(val)) {
      return val.length === 0 ? emptyValue : val;
    }
    if (typeof val === 'string') {
      return val.trim() === '' ? emptyValue : val;
    }
    return val;
  };

  // Function to escape CSV values (handles commas, quotes, and array formatting)
  function escapeCsvValue(value: any, emptyValue?: string): string {
    const safe = safeValue(value, emptyValue);

    if (Array.isArray(safe)) {
      value = JSON.stringify(safe);
    } else {
      value = String(safe);
    }

    const escaped = value.replace(/"/g, '""');
    return `"${escaped}"`;
  }

  let csvContent = '';

  // Filter Section
  csvContent += t('filtersUsed') + '\n';
  appliedFilters.forEach((filter) => {
    csvContent += `${escapeCsvValue(filter.label)},${escapeCsvValue(filter.value)}\n`;
  });

  csvContent += '\n' + t('chartData') + '\n';

  // Create Header Row
  const seriesNames = series.map((s: any) => s.name);
  let header = [categoryName, ...seriesNames].map((value) => escapeCsvValue(value)).join(',');
  csvContent += header + '\n';

  // Create Data Rows
  categories.forEach((category: any, catIndex: number) => {
    const row = [escapeCsvValue(category)];
    series.forEach((s) => {
      const dataValue =
        s.data[catIndex] !== undefined && s.data[catIndex] !== null ? s.data[catIndex] : '';
      row.push(escapeCsvValue(dataValue, t('noDataAvailable')));
    });

    csvContent += row.join(',') + '\n';
  });

  downloadCSV(csvContent, fileName);
};

export const downloadCSV = (csvContent: string, fileName: string) => {
  const contentWithBom = csvContent.startsWith('\uFEFF') ? csvContent : '\uFEFF' + csvContent;
  const blob = new Blob([contentWithBom], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName + `_${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const downloadCSVFromUrl = (url: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
